import { Typography, Box, TextField, Autocomplete, Button, Spinner } from "@kamstrup/kfl";
import { FC, useState } from "react";
import {
  PasswordStrength,
  PasswordStrengthMeter
} from "../passwordStrengthMeter/PasswordStrengthMeter";
import { CenteredCard } from "../../containers/card";
import { useCaStates } from "../../hooks/useCaStates/useCaStates";
import { useCountries } from "../../hooks/useCountries/useCountries";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { useUsStates } from "../../hooks/useUsStates/useUsStates";
import { CountryDto } from "../../models/countryDto";
import { SignupUserDto } from "../../models/signupUserDto";
import { StateDto } from "../../models/stateDto";
import { commonStyles } from "../../styles/commonStyles";
import { getLan } from "../../utils/languageManager";
import { toBase64 } from "../../utils/passwordEncoder/base64Encoder";

interface CompleteRegistrationStepProps {
  onSubmit: (dto: SignupUserDto) => Promise<void>;
  onCancel: () => Promise<void>;
  loading?: boolean;
  email: string;
  workflowId: string;
  languageOverride?: string | null;
}

export const CompleteRegistrationStep: FC<CompleteRegistrationStepProps> = ({
  onSubmit,
  onCancel,
  loading,
  email,
  workflowId,
  languageOverride
}) => {
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<CountryDto | null>(null);
  const [selectedState, setSelectedState] = useState<StateDto | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(
    PasswordStrength.TooWeak
  );
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>("");
  const [passwordLengthError, setPasswordLengthError] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { translateString } = useTranslation();
  const { countries } = useCountries();
  const { states: usStates } = useUsStates();
  const { states: caStates } = useCaStates();

  const validateName = (name: string): boolean =>
    !!name && name.trim().length > 0 && name.trim().length <= 64;

  const validatePhone = (phone: string): boolean =>
    !!phone && phone.trim().length >= 6 && phone.trim().length <= 20 && /\d/.test(phone);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) newErrors.firstName = translateString("first_name_required");
    if (!lastName.trim()) newErrors.lastName = translateString("last_name_required");
    if (!validatePhone(phoneNumber))
      newErrors.phoneNumber = translateString("validation_phone_invalid_format");
    if (!newPassword) newErrors.password = translateString("password_required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isSubmitEnabled =
    validateName(firstName) &&
    validateName(lastName) &&
    validatePhone(phoneNumber) &&
    passwordsMatch &&
    (passwordStrength === PasswordStrength.Good || passwordStrength === PasswordStrength.Strong) &&
    !!selectedCountry &&
    !passwordLengthError;

  const handleSubmit = async () => {
    if (isSubmitting || loading) return;
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const encodedPassword = toBase64(newPassword);
      const languageToSend = languageOverride ?? getLan();

      const signupUserPayload: SignupUserDto = {
        email,
        firstname: firstName.trim(),
        lastname: lastName.trim(),
        country: selectedCountry?.twoLetterCode.code,
        state: selectedState?.twoLetterCode.code,
        password: encodedPassword,
        phoneNumber,
        workflowId,
        language: languageToSend
      };

      await onSubmit(signupUserPayload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => onCancel();

  return (
    <CenteredCard title={translateString("complete_registration")}>
      <Typography variant="body4" sx={commonStyles.introText}>
        {translateString("complete_registration_description")}
      </Typography>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Box sx={commonStyles.textFieldContainer}>
          <TextField
            size="large"
            label={translateString("first_name")}
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            inputProps={{ maxLength: 64 }}
            error={!!errors.firstName}
            helperText={errors.firstName}
          />
        </Box>

        <Box sx={commonStyles.textFieldContainer}>
          <TextField
            size="large"
            label={translateString("last_name")}
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            inputProps={{ maxLength: 64 }}
            error={!!errors.lastName}
            helperText={errors.lastName}
          />
        </Box>

        <Autocomplete
          fullWidth
          options={countries as CountryDto[]}
          getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
          isOptionEqualToValue={(option, value) =>
            option.twoLetterCode.code === value.twoLetterCode.code
          }
          value={selectedCountry}
          onChange={(event, newValue) => {
            setSelectedCountry(newValue as CountryDto | null);
            setSelectedState(null);
            setPhoneNumber(
              (newValue as CountryDto)?.dialCode?.dialcode
                ? `+${(newValue as CountryDto).dialCode.dialcode}`
                : ""
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translateString("country")}
              InputLabelProps={{ shrink: true }}
            />
          )}
          sx={commonStyles.autoComplete}
        />

        {selectedCountry && selectedCountry.twoLetterCode.code === "US" && (
          <Box sx={commonStyles.textFieldContainer}>
            <Autocomplete
              options={usStates}
              value={selectedState}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
              isOptionEqualToValue={(option, value) =>
                option.twoLetterCode.code === value.twoLetterCode.code
              }
              onChange={(event, newValue) => setSelectedState(newValue as StateDto | null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translateString("state")}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              sx={commonStyles.autoComplete}
            />
          </Box>
        )}

        {selectedCountry && selectedCountry.twoLetterCode.code === "CA" && (
          <Box sx={commonStyles.textFieldContainer}>
            <Autocomplete
              options={caStates}
              value={selectedState}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
              isOptionEqualToValue={(option, value) =>
                option.twoLetterCode.code === value.twoLetterCode.code
              }
              onChange={(event, newValue) => setSelectedState(newValue as StateDto | null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translateString("state")}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              )}
              sx={commonStyles.autoComplete}
            />
          </Box>
        )}

        <Box sx={commonStyles.textFieldContainer}>
          <TextField
            size="large"
            label={translateString("direct_phone_number")}
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value.trim())}
            error={phoneNumber.length > 0 && !validatePhone(phoneNumber)}
            helperText={errors.phoneNumber}
          />
        </Box>

        <Box sx={commonStyles.textFieldContainer}>
          <PasswordStrengthMeter
            onStrengthChange={setPasswordStrength}
            onPasswordsMatch={setPasswordsMatch}
            onPasswordChange={setNewPassword}
            onLengthExceed={setPasswordLengthError}
            passwordField={translateString("password")}
            confirmPasswordField={translateString("confirm_password")}
          />
          {errors.password && (
            <Typography color="error" variant="caption">
              {errors.password}
            </Typography>
          )}
        </Box>

        {errors.general && (
          <Box sx={{ marginBottom: 2 }}>
            <Typography color="error" variant="body2" textAlign="center">
              {errors.general}
            </Typography>
          </Box>
        )}

        <Box sx={commonStyles.buttonContainer}>
          <Button
            type="submit"
            startIcon={loading || isSubmitting ? <Spinner /> : undefined}
            color="primary"
            variant="contained"
            fullWidth
            sx={commonStyles.button}
            disabled={!isSubmitEnabled || loading || isSubmitting}
          >
            {loading || isSubmitting
              ? translateString("submitting")
              : translateString("submit_registration")}
          </Button>
        </Box>

        <Box sx={commonStyles.buttonContainer}>
          <Typography
            sx={{ ...commonStyles.linkText, color: "#EE2A20" }}
            textAlign="center"
            variant="body3"
            onClick={handleCancel}
          >
            {translateString("cancel")}
          </Typography>
        </Box>
      </form>
    </CenteredCard>
  );
};
